import { systemLabel } from 'helpers/systemBets';
import { slipKindLabel } from 'utils/slip.utils';
import { Event, EventBet, Slip } from 'types/Slip';
import styles from './SlipEvents.module.css';
import { TicketMultiBonus } from 'components/MultiBonus/MultiBonus';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { describePick } from 'helpers/pickLabel';
import { PickSummary } from 'components/PickSummary/PickSummary';
import classNames from 'classnames';
import { CURRENCY } from 'App';
import { formatDateTime } from 'helpers/dateHelper';
import { useState } from 'react';
import { ThemeIcon } from 'components/ThemeIcon/ThemeIcon';
import { ReplayRemaining } from 'components/ReplayRemaining/ReplayRemaining';
import { usePlayerLayout } from 'lib/playerLayout';
import { CashoutButton } from 'components/Cashout/CashoutButton';
import { StornoButton } from 'components/Storno/StornoButton';
import { CashoutOffer } from 'services/api/slipService';

interface Props {
  slip: Slip;
  defaultExpanded?: boolean;
  /** The open ticket's cash out offer, if cash out is on. */
  cashout?: CashoutOffer;
  onCashedOut?: () => void;
}

/** One combination's share of the stake (see slip.utils). */
const stakePerBet = (slip: Slip) =>
  (Number(slip.stake) / Math.max(slip.combination, 1)).toFixed(2);

export const SlipEvents = ({
  slip,
  defaultExpanded = false,
  cashout,
  onCashedOut = () => {},
}: Props) => {
  const [showMore, setShowMore] = useState(defaultExpanded);
  const { t } = useTranslation();
  const slipStatus = String(slip.status ?? '').toLowerCase();
  const modern = usePlayerLayout() === 'modern';

  const rows =
    slip.event_bets &&
    slip.event_bets.length > 0 &&
    slip.event_bets.map((x, index) => {
      if (x.bets.length === 1) {
        return (
          <div key={index}>
            <EventBetInfo
              event={x.event}
              bet={x.bets[0]}
              evenRow={!!(index % 2)}
              renderTime
              t={t}
            />
          </div>
        );
      }
      return x.bets.map((y, i) => (
        <div key={`${index}-${i}`}>
          <EventBetInfo
            event={x.event}
            bet={y}
            index={i}
            renderTime={!i}
            t={t}
          />
        </div>
      ));
    });

  const details = (
    <div className={styles.moreDetails}>
      <span>{t(slip.systemSizes ? 'system_label' : 'combination_label')}</span>
      <span>
        {slip.systemSizes ? (
          <>
            {systemLabel(slip.systemSizes, slip.event_bets.length)} (
            {slip.combination})
          </>
        ) : slip.combination === 1 ? (
          <>(1)</>
        ) : (
          <>Multi ({slip.combination})</>
        )}
      </span>
      <span>{t('number_of_events_label')}</span>
      <span>{slip.event_bets.length}</span>
      <span>{t('number_of_bets_label')}</span>
      <span>{slip.combination}</span>
      <span>{t('stake_per_bet_label')}</span>
      <span>{stakePerBet(slip)}</span>
      <span>{t('total_stake_label')}</span>
      <span>
        {slip.stake} {CURRENCY}
      </span>
      <span>{t('max_coefficient_label')}</span>
      <span>{slip.maxCoefficient.toFixed(2)}</span>
      <span>{t('max_gain_label')}</span>
      <span>
        {slip.payment} {CURRENCY}
      </span>
    </div>
  );

  if (modern) {
    return (
      <div className={classNames(styles.modernTicket, styles[slipStatus])}>
        <div className={styles.modernHead}>
          <span className={styles.modernStatusDot} aria-hidden />
          <div className={styles.modernTitle}>
            <strong>
              {t('bet_slip_number_label')} {slip.id} –{' '}
              <span className={styles.modernStatusText}>
                {t(`${slipStatus}_label`, { defaultValue: slipStatus })}
              </span>
              {slip.is_live_ticket && (
                <span className={styles.modernLive}>LIVE</span>
              )}
            </strong>
            <small>{slip.created_at}</small>
            {slip.awaiting_other_matches && (
              <small className={styles.modernNote}>
                {t('awaiting_other_matches_label')}
              </small>
            )}
          </div>
          <div className={styles.modernStats}>
            <div>
              <strong>
                {slip.stake} {CURRENCY}
              </strong>
              <small>{t('payment_label')}</small>
            </div>
            <div>
              <strong>{slip.maxCoefficient.toFixed(2)}</strong>
              <small>{t('max_coefficient_label')}</small>
            </div>
            <div>
              <strong>
                {slip.payment} {CURRENCY}
              </strong>
              <small>{t('max_gain_label')}</small>
            </div>
          </div>
          <TicketMultiBonus
            percent={slip.multi_bonus_percent}
            amount={slip.multi_bonus_amount}
          />
          <ReplayRemaining slip={slip} />
          <StornoButton slip={slip} onCancelled={onCashedOut} />
          <CashoutButton offer={cashout} onCashedOut={onCashedOut} />
          <button
            type="button"
            className={styles.modernToggle}
            aria-expanded={showMore}
            aria-label={t('details_label', { defaultValue: 'Details' })}
            onClick={() => setShowMore(!showMore)}
          >
            {showMore ? '▴' : '▾'}
          </button>
        </div>
        {showMore && details}
        <div className={styles.eventsHeader}>
          <div className={styles.time}>{t('time_label')}</div>
          <div className={styles.event}>{t('event_label')}</div>
          <div className={styles.tip}>{t('tip_label')}</div>
          <div className={styles.quota}>{t('quota_label')}</div>
          <div className={styles.result}>{t('result_label')}</div>
        </div>
        <div className={styles.modernRows}>{rows}</div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.slipInfo}>
        <div className={styles.slipNumber}>
          <ThemeIcon icon="record" alt="" variant="inline" />
          <span className={styles[slipStatus]}>
            {t('bet_slip_number_label')} {slip.id} -{' '}
            <span>
              {t(`${slipStatus}_label`, { defaultValue: slipStatus })}{' '}
              {slip.awaiting_other_matches && (
                <>({t('awaiting_other_matches_label')}) </>
              )}
              {slip.is_live_ticket && '*LIVE*'}
            </span>
          </span>
        </div>
        <ReplayRemaining slip={slip} />
        <div className={styles.cashOut}>
          <StornoButton slip={slip} onCancelled={onCashedOut} />
          <CashoutButton offer={cashout} onCashedOut={onCashedOut} />
        </div>
      </div>
      <div className={styles.header}>
        <span>
          {t('date_label')}: {slip.created_at}
        </span>
        <span>{slipKindLabel(slip, t)}</span>
        <span>
          {t('payment_label')} {slip.stake} {CURRENCY}
        </span>
        <span>
          {t('max_gain_label')} {slip.payment} {CURRENCY}
        </span>
        <TicketMultiBonus
          percent={slip.multi_bonus_percent}
          amount={slip.multi_bonus_amount}
          compact
        />
        <ThemeIcon icon="printer" alt="Print" variant="inline" tone="chrome" />
        <ThemeIcon
          icon="chevron-right-white"
          alt="Show Details"
          variant="inline"
          tone="chrome"
          onClick={() => setShowMore(!showMore)}
        />
      </div>
      <div className={styles.eventsHeader}>
        <div className={styles.time}>{t('time_label')}</div>
        <div className={styles.event}>{t('event_label')}</div>
        <div className={styles.tip}>{t('tip_label')}</div>
        <div className={styles.quota}>{t('quota_label')}</div>
        <div className={styles.result}>{t('result_label')}</div>
      </div>
      {slip.event_bets &&
        slip.event_bets.length > 0 &&
        slip.event_bets.map((x, index) => {
          if (x.bets.length === 1) {
            return (
              <div key={index}>
                <EventBetInfo
                  event={x.event}
                  bet={x.bets[0]}
                  evenRow={!!(index % 2)}
                  renderTime
                  t={t}
                />
              </div>
            );
          } else {
            return x.bets.map((y, index) => (
              <div key={index}>
                <EventBetInfo
                  event={x.event}
                  bet={y}
                  index={index}
                  renderTime={!index}
                  t={t}
                />
              </div>
            ));
          }
        })}
      {showMore && (
        <div className={styles.moreDetails}>
          <span>{t('number_of_events_label')}</span>
          <span>{slip.event_bets.length}</span>
          <span>{t('number_of_bets_label')}</span>
          <span>{slip.combination}</span>
          <span>{t('stake_per_bet_label')}</span>
          <span>{stakePerBet(slip)}</span>
          <span>{t('total_stake_label')}</span>
          <span>
            {slip.stake} {CURRENCY}
          </span>
          <span>{t('max_coefficient_label')}</span>
          <span>{slip.maxCoefficient.toFixed(2)}</span>
          <span>{t('max_gain_label')}</span>
          <span>
            {slip.payment} {CURRENCY}
          </span>
        </div>
      )}
    </div>
  );
};

interface EventBetInfoProps {
  event: Event;
  bet: EventBet;
  t: TFunction;
  evenRow?: boolean;
  renderTime?: boolean;
  index?: number;
}

const normalizeScoreText = (value?: string | null): string => {
  if (!value) return '';
  return String(value).replace(/-/g, ':').replace(/\s+/g, ' ').trim();
};

const formatHalfBreakdown = (value?: string | null): string => {
  const normalized = normalizeScoreText(value);
  if (!normalized) return '';
  const parts = normalized
    .split(/[;,|]/)
    .map((x) => x.trim())
    .filter(Boolean);
  if (parts.length >= 2) {
    return `(${parts[0]}; ${parts[1]})`;
  }
  return `(${normalized})`;
};

const pickHalfBreakdownSource = (event: Event): string | null => {
  if (event.currentResultSecond) return event.currentResultSecond;
  if (event.resultAtCreationSecond) return event.resultAtCreationSecond;
  const maybeCurrent = normalizeScoreText(event.currentResult);
  if (/[;,|]/.test(maybeCurrent)) return maybeCurrent;
  return null;
};

const EventBetInfo = ({
  event,
  bet,
  t,
  evenRow = false,
  renderTime = false,
  index,
}: EventBetInfoProps) => {
  const status = String(bet.status ?? '').toLowerCase();
  const isWon = status === 'winning' || status === 'won' || status === 'win';
  const isLost = status === 'failed' || status === 'lost' || status === 'lose';
  const isPending =
    status === 'pending' || status === 'open' || status === 'waiting';
  const hasFinalResult =
    event.result.homeGoals !== null &&
    event.result.homeGoals !== undefined &&
    event.result.awayGoals !== null &&
    event.result.awayGoals !== undefined;
  const halfBreakdown = formatHalfBreakdown(pickHalfBreakdownSource(event));
  const goalsTimeline = Array.isArray(event.goalEvents)
    ? event.goalEvents.filter(Boolean)
    : [];
  const resultTooltip =
    hasFinalResult || halfBreakdown
      ? `FT: ${hasFinalResult ? `${event.result.homeGoals}:${event.result.awayGoals}` : '-'}${
          halfBreakdown ? `\nBy periods: ${halfBreakdown}` : ''
        }${goalsTimeline.length ? `\nGoals:\n${goalsTimeline.join('\n')}` : ''}`
      : undefined;

  return (
    <div
      className={classNames(styles.events, styles[bet.status], {
        [styles['evenRow']]: evenRow,
        [styles.rowWon]: isWon,
        [styles.rowLost]: isLost,
      })}
    >
      <div className={styles.time}>
        {renderTime &&
          event.time > 0 &&
          formatDateTime(new Date(event.time * 1000), {
            removeYear: true,
            removeSeconds: true,
          })}
      </div>

      <div className={styles.event}>
        {index != null && (
          <span style={{ fontWeight: 700 }}>
            {String.fromCharCode(index + 65)}
          </span>
        )}
        {index && index > 0 ? (
          <span className={styles.multiCombination}> {index + 1}. Multi</span>
        ) : (
          <>
            {event.home} - {event.away}{' '}
            {event.isLive && (
              <span className={styles.resultAtCreation}>
                *LIVE* {event.resultAtCreation?.replace('-', ':')}
              </span>
            )}
          </>
        )}
      </div>
      <div className={styles.tip}>
        <PickSummary
          info={describePick(
            {
              betName: bet.betName,
              odd: bet.odd,
              home: event.home,
              away: event.away,
            },
            t
          )}
          odds={bet.odd.odds}
          match={`${event.home} - ${event.away}`}
        />
      </div>
      <div className={styles.quota}>{bet.odd.odds.toFixed(2)}</div>
      <div className={styles.result} title={resultTooltip}>
        {!index && hasFinalResult
          ? `${event.result.homeGoals}:${event.result.awayGoals}`
          : ''}
        {!index && halfBreakdown ? (
          <span className={styles.resultBreakdown}> {halfBreakdown}</span>
        ) : null}
      </div>
      {!hasFinalResult && isPending && (
        <ThemeIcon icon="clock" alt="Clock" variant="inline" />
      )}
      <div
        className={classNames(styles.statusMark, {
          [styles.statusWon]: isWon,
          [styles.statusLost]: isLost,
          [styles.statusPending]: isPending,
        })}
        aria-label={`Bet status ${status || 'unknown'}`}
      >
        {isWon ? '✓' : isLost ? '✕' : '•'}
      </div>
    </div>
  );
};
